// Müşteriye özel view-as ayarı — "sadece Osman için aç".
//
// Genel şalterin (ViewAsControl) altında durur. Listelenen müşteri için karar
// bu satırdır; listede olmayan müşteri genel şaltere uyar. Kuralın kendisi
// sunucuda (backend/app/services/view_as_policy.py); bu ekran yalnızca satırları
// okur, yazar ve siler.
//
// Yeni eklenen müşteri AÇIK eklenir: bu bölümün var olma sebebi tek müşteride
// denemek. Kapalı istenirse aynı satırdaki düğmeyle kapatılır.
//
// Genel şalterle aynı ilke: iyimser güncelleme yok, gösterilen durum her zaman
// sunucudan gelir.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { OrgSearchModal } from '../../components/OrgSearchModal'
import { useT } from '../../i18n'
import { viewAsOverridesI18n } from '../../i18n/settings/view-as'
import { apiUrl } from '../../lib/api'
import type { DirectoryOrg } from '../../mocks/data/organizations'
import type { OrgViewSide } from '../../lib/orgViewState'

const CONFIG_KEY = 'view_as.enabled'
const QUERY_KEY = ['admin', 'config', CONFIG_KEY, 'orgs'] as const
const BASE = `/admin/config/${CONFIG_KEY}/orgs`

type ViewAsOverride = {
  orgId: string
  orgName: string
  orgType: string
  side: string
  enabled: boolean
}

type ApiOverride = {
  org_id: string
  org_name: string
  org_type: string
  side: string
  config_value: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toOverride(item: ApiOverride): ViewAsOverride {
  return {
    orgId: item.org_id,
    orgName: item.org_name,
    orgType: item.org_type,
    side: item.side,
    // Sunucuyla aynı: yalnızca açıkça true olan açık sayılır.
    enabled: isRecord(item.config_value) && item.config_value.value === true,
  }
}

async function readOverrides(): Promise<ViewAsOverride[]> {
  const res = await fetch(apiUrl(BASE))
  if (!res.ok) throw new Error(`view_as overrides read failed: ${res.status}`)
  const data = (await res.json()) as { items: ApiOverride[] }
  return data.items.map(toOverride)
}

async function writeOverride(args: { orgId: string; enabled: boolean }): Promise<void> {
  const res = await fetch(apiUrl(`${BASE}/${args.orgId}`), {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ config_value: { value: args.enabled }, value_type: 'boolean' }),
  })
  if (!res.ok) throw new Error(`view_as override write failed: ${res.status}`)
}

async function removeOverride(orgId: string): Promise<void> {
  const res = await fetch(apiUrl(`${BASE}/${orgId}`), { method: 'DELETE' })
  if (!res.ok) throw new Error(`view_as override delete failed: ${res.status}`)
}

export function ViewAsOrgOverrides() {
  const t = useT(viewAsOverridesI18n)
  const queryClient = useQueryClient()
  const [pickerSide, setPickerSide] = useState<OrgViewSide | null>(null)

  const { data, isError, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: readOverrides,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })
  const write = useMutation({ mutationFn: writeOverride, onSuccess: refresh })
  const remove = useMutation({ mutationFn: removeOverride, onSuccess: refresh })
  const busy = write.isPending || remove.isPending

  const overrides = data ?? []

  function handlePick(org: DirectoryOrg) {
    setPickerSide(null)
    write.mutate({ orgId: org.orgId, enabled: true })
  }

  return (
    <section
      className="mt-4 rounded-[var(--radius-panel)] border border-[var(--color-border)] p-4"
      data-testid="view-as-org-overrides"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h3>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPickerSide('seller')}
            disabled={busy}
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
          >
            {t.addSeller}
          </button>
          <button
            type="button"
            onClick={() => setPickerSide('supply')}
            disabled={busy}
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
          >
            {t.addSupplier}
          </button>
        </div>
      </div>

      {isError ? (
        <p className="mt-4 text-sm text-[var(--color-danger)]" role="status">
          {t.loadError}
        </p>
      ) : isLoading ? null : overrides.length === 0 ? (
        <p className="mt-4 text-sm text-[var(--color-text-muted)]">{t.empty}</p>
      ) : (
        <ul className="mt-4 divide-y divide-[var(--color-border)] rounded-md border border-[var(--color-border)]">
          {overrides.map((o) => (
            <li key={o.orgId} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-[var(--color-text)]">
                  {o.orgName}
                </div>
                <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                  {o.side === 'supply' ? t.sideSupplier : t.sideSeller} · {o.orgType}
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label={t.switchLabel(o.orgName)}
                  checked={o.enabled}
                  disabled={busy}
                  onChange={(e) => write.mutate({ orgId: o.orgId, enabled: e.target.checked })}
                  className="h-5 w-5 cursor-pointer"
                />
                <span className="w-12 text-xs text-[var(--color-text-muted)]">
                  {o.enabled ? t.on : t.off}
                </span>
              </label>
              <button
                type="button"
                onClick={() => remove.mutate(o.orgId)}
                disabled={busy}
                aria-label={t.removeLabel(o.orgName)}
                title={t.followsGlobal}
                className="text-xs font-medium text-[var(--color-text-muted)] underline-offset-2 hover:text-[var(--color-text)] hover:underline disabled:opacity-50"
              >
                {t.remove}
              </button>
            </li>
          ))}
        </ul>
      )}

      {(write.isError || remove.isError) && (
        <p className="mt-2 text-xs text-[var(--color-danger)]" role="alert">
          {t.saveError}
        </p>
      )}

      <OrgSearchModal
        side={pickerSide}
        open={pickerSide !== null}
        onClose={() => setPickerSide(null)}
        onSelect={handlePick}
        actionLabel={t.addAction}
        description={t.pickerDescription}
      />
    </section>
  )
}
